'use client';

/**
 * ZineButton — the canonical action button used at the bottom of
 * every detail modal and form: EDIT (solid ink), CLOSE (outlined),
 * delete-icon (red outlined), share-icon (outlined ink).
 *
 * Three variants:
 *   solid     — ink fill, paper-1 text, ink border. The primary CTA
 *               (EDIT, SAVE, DONE). Default.
 *   outlined  — transparent bg, ink text, ink border. The secondary
 *               action (CLOSE, CANCEL, BACK).
 *   danger    — transparent bg, --over color (red), --over border.
 *               The destructive action (DELETE, REMOVE).
 *
 * `inverse` (build 1647) flips ink and paper for use on a black or ink
 * background — the photo lightbox's action row. solid becomes a paper
 * fill with ink text, outlined becomes a thin paper hairline with paper
 * text; danger is already red-on-transparent and stays as it is. Same
 * typography, same sizes, so a button on the dark screen still reads as
 * "one of ours" (Veronika, Telegram 6985: "a dark version, more minimal
 * elegant").
 *
 * Layout:
 *   default — `flex: 1`, expands to fill the row. Wrap multiple in a
 *             flex row with `gap: 8` for the standard action bar.
 *   icon=true — `width: 42`, centers content. For icon-only buttons
 *               (delete-X, share, ...).
 *   fullWidth=true — takes the full available width as a prominent primary
 *                    action (e.g. "Mark as Finished" on project detail).
 *
 * Always renders as a real `<button>`. Pass `onClick`, `type`,
 * `disabled`, `aria-label`, `title` — they all forward through.
 *
 * Override the typography or sizing via `style` for one-offs;
 * everything else lives in the variant.
 *
 * Replaces ~25 hand-rolled action-button sites across YarnDetailModal,
 * NeedleDetailModal, ProjectNeedleDetailModal, ProjectForm,
 * TechniqueForm, inspiration / techniques detail modals, plus the
 * occasional bare action button in pages.
 */

import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';

export type ZineButtonVariant = 'solid' | 'outlined' | 'danger';

export type ZineButtonSize = 'default' | 'small';

export type ZineButtonProps = {
  children: ReactNode;
  variant?: ZineButtonVariant;
  /**
   * Typographic scale. `default` is 14px / 0.06em tracking. `small` is
   * 11px / 0.1em — for buttons in dense settings panels (sync, debug
   * logs, danger-zone reset) where the full-size label crowds the row.
   */
  size?: ZineButtonSize;
  /** Fixed 42px width + centered content instead of flex: 1. For icon-only buttons. */
  icon?: boolean;
  /** Makes the button take the full available width (used for primary state-change actions like Mark as Finished). */
  fullWidth?: boolean;
  /** Ink ↔ paper swap for black / ink backgrounds (the photo lightbox). */
  inverse?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  'aria-label'?: string;
  /**
   * For buttons that toggle rather than fire — the chart editor's paint /
   * fill / erase tools, where `solid` shows the chosen one. Declared
   * explicitly: TypeScript does NOT check hyphenated JSX attributes on
   * components, so an undeclared one is dropped without a word.
   */
  'aria-pressed'?: boolean;
  title?: string;
  style?: CSSProperties;
  className?: string;
  /** Stable test selector — use for Playwright getByTestId. */
  'data-testid'?: string;
};

type VariantColors = { bg: string; color: string; border: string };

const VARIANT_COLORS: Record<ZineButtonVariant, VariantColors> = {
  solid:    { bg: 'var(--ink)',     color: 'var(--paper-1)', border: 'var(--ink)' },
  outlined: { bg: 'transparent',    color: 'var(--ink)',     border: 'var(--ink)' },
  danger:   { bg: 'transparent',    color: 'var(--over)',    border: 'var(--over)' },
};

/** The same three variants on a black / ink background. */
const INVERSE_COLORS: Record<ZineButtonVariant, VariantColors> = {
  solid:    { bg: 'var(--paper-1)', color: 'var(--ink)',     border: 'var(--paper-1)' },
  outlined: { bg: 'transparent',    color: 'var(--paper-1)', border: 'var(--paper-1)' },
  danger:   { bg: 'transparent',    color: 'var(--over)',    border: 'var(--over)' },
};

export function ZineButton({
  children,
  variant = 'solid',
  size = 'default',
  icon = false,
  fullWidth = false,
  inverse = false,
  type = 'button',
  onClick,
  disabled,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  title,
  style,
  className,
  'data-testid': dataTestid,
}: ZineButtonProps) {
  const c = (inverse ? INVERSE_COLORS : VARIANT_COLORS)[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      title={title}
      data-testid={dataTestid}
      className={className ? `zine-press ${className}` : 'zine-press'}
      style={{
        // All variants share inline-flex centering so any combination
        // of children (text only, icon only, icon + text fragment) lays
        // out the same: vertically centered with a 6px gap between
        // adjacent inline elements. Fixes the "✓ floating in the
        // upper-left corner" symptom Veronika flagged 2026-05-18 on the
        // MARK AS FINISHED / REOPEN PROJECT buttons (Telegram msg 3919
        // + 3930).
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        ...(icon
          ? { width: 42, padding: 0 }
          : fullWidth
          ? { width: '100%', padding: '12px 0' }
          : { flex: 1, padding: '10px 0' }),
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
        fontWeight: 800,
        fontSize: size === 'small' ? 11 : 14,
        letterSpacing: size === 'small' ? '0.1em' : '0.06em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        // NOT `opacity` inline: an inline value outranks `.zine-press:active`
        // and would silently cancel the press feedback. Both the disabled and
        // the pressed opacity live in globals.css.
        
        borderRadius: 0,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
